import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import RecordMetadata from '@/components/RecordMetadata';
import type { AuditProfile } from '@prolarus/shared';

const externalSystemSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255, 'Name must be 255 characters or less'),
  description: z.string().optional(),
});

type ExternalSystemFormValues = z.infer<typeof externalSystemSchema>;

export interface ExternalSystemRecord {
  id: string;
  name: string;
  description: string | null;
  created?: string | null;
  modified?: string | null;
  created_by_profile?: AuditProfile | null;
  updated_by_profile?: AuditProfile | null;
}

interface ExternalSystemFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  externalSystem: ExternalSystemRecord | null;
  onSave: (data: Omit<ExternalSystemRecord, 'id'>) => Promise<void>;
  onSaveAndNew?: (data: Omit<ExternalSystemRecord, 'id'>) => Promise<void>;
}

export default function ExternalSystemFormDialog({
  open,
  onOpenChange,
  externalSystem,
  onSave,
  onSaveAndNew,
}: ExternalSystemFormDialogProps) {
  const isEditing = !!externalSystem;

  const form = useForm<ExternalSystemFormValues>({
    resolver: zodResolver(externalSystemSchema),
    defaultValues: {
      name: '',
      description: '',
    },
  });

  useEffect(() => {
    if (open) {
      if (externalSystem) {
        form.reset({
          name: externalSystem.name,
          description: externalSystem.description || '',
        });
      } else {
        form.reset({
          name: '',
          description: '',
        });
      }
    }
  }, [open, externalSystem, form]);

  const handleSubmit = async (values: ExternalSystemFormValues, andNew: boolean = false) => {
    const data = {
      name: values.name,
      description: values.description || null,
    };

    if (andNew && onSaveAndNew) {
      await onSaveAndNew(data);
      form.reset({ name: '', description: '' });
    } else {
      await onSave(data);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit External System' : 'New External System'}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit((v) => handleSubmit(v, false))} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., SAP, NetSuite, QuickBooks" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Optional description of this external system"
                      className="resize-none"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="pt-4 gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {isEditing ? 'Save Changes' : 'Save & Close'}
              </Button>
            </DialogFooter>

            {isEditing && (
              <RecordMetadata record={externalSystem} />
            )}
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
